import { type PropsWithChildren, useState } from 'react';
import { type RepubCountry } from '@cx-web/domain-types';
import { type RepubIs } from '@cx-web/utils';
import clsx from 'clsx';

import { type BookmakersConfig } from '@cx-modules/types';

import { GridTable, PerformanceIndicator, useToggle } from '~/components/cxy/cxy.ts';
import { type BetInsightsConfig } from '~/configuration/BetInsightsConfiguration';
import { type BettingInsightsContent, type PositiveEvIndex, type ValueBetResult } from '~/configuration/types';
import { type BettingInsightsUI, type StyleSheet } from '~/configuration/ui/BettingInsightsUI';
import { useTranslatedSubeventName } from '~/hooks/useTranslatedSubeventName';

import { DefaultTableBody } from '../DefaultTableBody';
import { EventDetails } from '../EventDetails';

import styles from './styles/PositiveEv.module.css';

const PositiveEvComponent = ({
  children,
  config,
  content,
  result,
}: {
  config: BetInsightsConfig<PositiveEvIndex, ValueBetResult>;
  content: BettingInsightsContent<PositiveEvIndex>[];
  result: ValueBetResult;
} & PropsWithChildren) => {
  const isCaptionEmpty = !config.ui.card.title.format;
  const subeventLabel = useTranslatedSubeventName(result.categoryGroupName, result.subeventName);

  return (
    <GridTable className={styles.positiveValue} label={subeventLabel} mobileColumns={3}>
      {config.ui.card.title.render && (
        <GridTable.Caption className={{ [styles.hidden]: isCaptionEmpty }}>
          <EventDetails<PositiveEvIndex> content={content} renderTitle={config.ui.card.title.render} result={result} />
        </GridTable.Caption>
      )}
      {children}
    </GridTable>
  );
};

export function PositiveEvBody({
  bookmakers,
  content,
  label,
  resultIndex,
  styles,
  table,
}: {
  bookmakers: BookmakersConfig;
  content: ValueBetResult;
  label: string;
  resultIndex: number;
  styles: StyleSheet;
  table: BettingInsightsUI<PositiveEvIndex, BettingInsightsContent<PositiveEvIndex>>['card']['table'];
}) {
  return (
    <DefaultTableBody
      bookmakers={bookmakers}
      content={content}
      label={label}
      resultIndex={resultIndex}
      styles={styles}
      // @ts-expect-error - TypeScript structural typing false positive: factory-created table type is structurally identical but seen as unrelated due to module boundary inference. See docs/refactoring-config-to-functional.md for details.
      table={table}
    />
  );
}

export function PositiveEvFooter({
  content,
  formatDate,
  repubCountry,
  repubIs,
}: {
  content: ValueBetResult;
  formatDate?: (date: string, format?: string) => string;
  repubCountry: RepubCountry;
  repubIs: RepubIs;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const { Indicator, headingProps, sectionProps, triggerClass } = useToggle({
    isOpen,
    onChange: setIsOpen,
  });

  if (!content.sportsData?.history) {
    return null;
  }

  const lastTenResults = content.sportsData.history.slice(0, 10);

  return (
    <GridTable.Footer className={styles.footer} style={{ gridColumn: '1 / -1' }}>
      <GridTable.Row className={styles.footerRow}>
        <GridTable.Cell
          {...sectionProps}
          alignItems="flex-start"
          className={clsx(styles.footerCell, styles.footerSection, sectionProps.className)}
        >
          {content.betDescriptionMap?.[repubCountry] || content.betDescription}
        </GridTable.Cell>
        <GridTable.Cell {...headingProps} className={clsx(styles.indicatorCell, styles.footerCell, triggerClass)}>
          <PerformanceIndicator
            americanFormat={repubIs.US}
            formatDate={formatDate}
            history={lastTenResults}
            hitRate={content.sportsData.hitRates.last10}
            showMatchInformation={isOpen}
          />
          <span className={styles.indicator}>
            <Indicator />
          </span>
        </GridTable.Cell>
      </GridTable.Row>
    </GridTable.Footer>
  );
}

PositiveEvComponent.Body = PositiveEvBody;
PositiveEvComponent.Footer = PositiveEvFooter;

export { PositiveEvComponent as PositiveEv };
